import React, { ChangeEvent, FC, HTMLAttributes, useState } from 'react';
import {
  Autocomplete,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { RemoveIcon } from 'components';

import { UserSearchesV2Filter } from '../type';

interface Props {
  filter: UserSearchesV2Filter;
  setFilter: (filter: UserSearchesV2Filter) => void;
}

const Emails: FC<Props> = ({ filter, setFilter }) => {
  const { t } = useTranslation();
  const { emails, emailOptions } = filter;

  const [inputValue, setInputValue] = useState('');

  function onClickRemove() {
    setFilter({ ...filter, emails: [] });
  }

  function handleChange(event: ChangeEvent, newSelectedOptions: string[]) {
    setFilter({ ...filter, emails: newSelectedOptions });
  }

  function renderInput(params: AutocompleteRenderInputParams) {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setInputValue(value);
    };

    return (
      <TextField
        fullWidth
        value={inputValue}
        variant="outlined"
        onChange={handleChange}
        onBlur={() => setInputValue('')}
        placeholder={t('insights:advanced_filter_placeholder_users')}
        {...params}
      />
    );
  }

  function renderOption(
    props: HTMLAttributes<HTMLLIElement>,
    option: string,
    { selected }: AutocompleteRenderOptionState
  ) {
    return (
      <Grid component="li" container alignItems="center" justifyContent="space-between" {...props}>
        <Grid xs>
          <Typography noWrap>{option}</Typography>
        </Grid>
        <Grid>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={selected}
          />
        </Grid>
      </Grid>
    );
  }

  function renderTags(value: string[], getTagProps: AutocompleteGetTagProps) {
    return value.map((email, index) => (
      <Chip key={email + index} label={email} {...getTagProps({ index })} />
    ));
  }

  return (
    <Grid container alignItems="center">
      <Grid xs={2}>
        <Typography variant="body2">{t('common:email')}</Typography>
      </Grid>
      <Grid xs={9}>
        <Autocomplete
          multiple
          disableClearable
          disableCloseOnSelect
          renderInput={renderInput}
          renderOption={renderOption}
          renderTags={renderTags}
          options={emailOptions}
          inputValue={inputValue}
          onChange={handleChange}
        />
      </Grid>
      {!!emails.length && (
        <Grid xs={1}>
          <Box pl={1}>
            <RemoveIcon onClick={onClickRemove} />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default Emails;
